<template>
    <div data-testid="weekly-action-commitment" class="flex min-h-0 flex-1 flex-col overflow-hidden">
        <div class="m-3 flex min-h-0 flex-1 flex-col overflow-y-auto rounded-xl bg-surface-sunken">
            <div class="journeys-commitment-action-list flex flex-col" aria-label="Suggested weekly actions">
                <section
                    v-for="group in actionGroups"
                    :key="group.key"
                    class="journeys-commitment-action-group flex flex-col"
                    :aria-labelledby="`commitment-action-group-${group.key}`">
                    <h3 :id="`commitment-action-group-${group.key}`" class="journeys-commitment-action-group__label flex items-center gap-1">
                        <span class="size-2 shrink-0 rounded-full bg-journey-marker" aria-hidden="true"></span>
                        {{ group.title }}
                    </h3>
                    <ul class="flex flex-col gap-2.5">
                        <li v-for="action in group.actions" :key="action.id">
                            <WeeklyActionCard
                                :action="action"
                                :role-play-setup-url="rolePlaySetupUrl"
                                variant="selection"
                                :is-selected="selectedActionIds.includes(action.id)"
                                :selection-disabled="isLocked || isSubmitting"
                                @toggle-selection="toggleAction"
                                @view-details="(actionId) => emit('view-details', actionId)" />
                        </li>
                    </ul>
                </section>
            </div>
        </div>
        <WeeklyActionWorkflowFooter
            :title="`${committedActionCountLabel} for ${weekRange}`"
            description="These hold until your Friday check-in — pick what you can honestly do."
            action-label="Start my week"
            test-id="commit-weekly-actions"
            :disabled="isLocked || selectedActionIds.length === 0"
            :is-submitting="isSubmitting"
            @continue="commitActions" />
    </div>
</template>

<script setup lang="ts">
import type { CommitDevelopmentActionsRequest } from "~vue/api/generated/development-journeys";
import { computed, ref, watch } from "vue";

import WeeklyActionCard from "./WeeklyActionCard.vue";
import type { WeeklyAction } from "./weeklyActions";
import WeeklyActionWorkflowFooter from "./WeeklyActionWorkflowFooter.vue";

const props = withDefaults(
    defineProps<{
        actions: WeeklyAction[];
        rolePlaySetupUrl: string;
        weekRange: string;
        isSubmitting: boolean;
        isLocked?: boolean;
    }>(),
    {
        isLocked: false,
    },
);

const emit = defineEmits<{
    commit: [actionIds: CommitDevelopmentActionsRequest["action_ids"]];
    "update:committed-action-count": [count: number];
    "view-details": [actionId: string];
}>();

type ActionGroup = { key: string; title: string; actions: WeeklyAction[] };

const selectedActionIds = ref<string[]>([]);
const actionGroups = computed<ActionGroup[]>(() => groupActions(props.actions));
const committedActionCountLabel = computed(() => `${selectedActionIds.value.length} ${selectedActionIds.value.length === 1 ? "commitment" : "commitments"}`);

watch(
    () => props.actions.map((action) => action.id).join(","),
    () => {
        selectedActionIds.value = [];
    },
    { immediate: true },
);

watch(selectedActionIds, (actionIds) => emit("update:committed-action-count", actionIds.length), { immediate: true });

const toggleAction = (actionId: string) => {
    if (props.isLocked || props.isSubmitting) {
        return;
    }
    if (selectedActionIds.value.includes(actionId)) {
        selectedActionIds.value = selectedActionIds.value.filter((id) => id !== actionId);
        return;
    }
    selectedActionIds.value = [...selectedActionIds.value, actionId];
};

const commitActions = () => {
    if (props.isLocked || selectedActionIds.value.length === 0 || props.isSubmitting) {
        return;
    }
    emit("commit", selectedActionIds.value);
};

function groupActions(actions: WeeklyAction[]): ActionGroup[] {
    const groups = new Map<string, ActionGroup>();
    for (const action of actions) {
        const goal = action.development_goal;
        const key = goal?.id ?? "independent-actions";
        const title = goal?.title ?? "Actions";
        const group = groups.get(key) ?? { key, title, actions: [] };
        group.actions.push(action);
        groups.set(key, group);
    }
    return [...groups.values()];
}
</script>

<style scoped>
@reference "../../../../css/tailwind.css";

@layer components {
    .journeys-commitment-action-list {
        gap: 20px;
        padding: 20px;
    }

    .journeys-commitment-action-group {
        gap: 4px;
    }

    .journeys-commitment-action-group + .journeys-commitment-action-group {
        margin-top: 20px;
    }

    .journeys-commitment-action-group__label {
        @apply scaffold-details-small text-ink-muted;
    }
}
</style>
